/* Jediné místo, kde frontend sahá na realitní zakázky.
 *
 * PROČ ADAPTÉR: kontrakt s backendem je na jednom místě. Když se routa
 * přejmenuje, mění se tenhle soubor, ne pět komponent.
 *
 * ⚠️ FÁZE SE SEM NEUKLÁDÁ. `stav_index` je dopočítaný z OBCHODU (deals) a
 * chodí ze serveru. Frontend ho nikdy nepočítá sám a nikdy si ho nedrží
 * v odděleném stavu delší dobu, než trvá jedno volání — jinak by po přetažení
 * karty na /obchody ukazoval starou fázi.
 */
import api from "@/lib/api";

const ZAKLAD = "/api/zakazky";

/** Moje zakázky. Vrací { zakazky: [], faze: [] }. */
export async function nactiZakazky() {
  const { data } = await api.get(ZAKLAD);
  const zakazky = Array.isArray(data?.zakazky) ? data.zakazky : null;
  /* Ne-pole NIKDY neprojde dál jako prázdný seznam. Makléř by to přečetl jako
     „nic tu nemám“, založil zakázku znovu a měl v systému dva záznamy o jedné
     nemovitosti. */
  if (!zakazky) throw new Error("neocekavana-odpoved");
  return { zakazky: zakazky.filter(Boolean), faze: Array.isArray(data?.faze) ? data.faze : [] };
}

/**
 * Zakázka patřící k OBCHODU. Karta obchodního případu (`/obchody/:id`) zná
 * jen `deal_id` — nemovitost visí opačným směrem (`zakazky.deal_id`).
 *
 * ⚠️ Filtruje se i tady, ne jen na serveru. Parametr `deal_id` je na backendu
 * nový a Emergent nasazuje zvlášť; kdyby ho starý server ignoroval, přišel by
 * celý seznam a karta by ukázala CIZÍ nemovitost. Radši o jeden filtr víc než
 * adresa souseda na kartě klienta.
 *
 * Vrací `null`, když k obchodu žádná nemovitost není (typicky poptávka na
 * nákup). To NENÍ chyba — volající sekci prostě nevykreslí.
 */
export async function nactiZakazkuObchodu(dealId) {
  if (!dealId) return null;
  const { data } = await api.get(ZAKLAD, { params: { deal_id: dealId } });
  const zakazky = Array.isArray(data?.zakazky) ? data.zakazky : null;
  if (!zakazky) throw new Error("neocekavana-odpoved");
  return zakazky.find((z) => z && String(z.deal_id || "") === String(dealId)) || null;
}

export async function nactiZakazku(id) {
  const { data } = await api.get(`${ZAKLAD}/${id}`);
  if (!data || !data.id) throw new Error("neocekavana-odpoved");
  return data;
}

export async function zalozZakazku(payload) {
  const { data } = await api.post(ZAKLAD, payload);
  return data;
}

export async function upravZakazku(id, zmeny) {
  const { data } = await api.patch(`${ZAKLAD}/${id}`, zmeny);
  return data;
}

export async function smazZakazku(id) {
  const { data } = await api.delete(`${ZAKLAD}/${id}`);
  return data;
}

/** Posun po procesní mapě. Server zapíše do obchodu a vrátí novou zakázku. */
export async function posunFazi(id, stageIndex) {
  const { data } = await api.post(`${ZAKLAD}/${id}/faze`, { stage_index: stageIndex });
  return data;
}

/**
 * „Co po klientovi chceme, na jedno kliknutí."
 * Vrací { url_path, zprava, predmet, plati_do, email_majitele, odeslano }.
 * `zprava` obsahuje `{odkaz}` — dosazuje ho volající (viz sZakladem níže),
 * protože adresu, na které appka běží, zná jistě jen prohlížeč.
 */
export async function vyzvaPodklady(id, { odeslat = false, jazyk, platnostDnu } = {}) {
  const { data } = await api.post(`${ZAKLAD}/${id}/podklady`, {
    odeslat, jazyk, platnost_dnu: platnostDnu,
  });
  return data;
}

/** Uvítací zpráva majiteli + plán marketingových kroků. */
export async function uvitani(id, { odeslat = false, jazyk } = {}) {
  const { data } = await api.post(`${ZAKLAD}/${id}/uvitani`, { odeslat, jazyk });
  return data;
}

/** Dosadí do zprávy plnou adresu odkazu. Bez toho by klientovi přišlo „{odkaz}". */
export function sZakladem(zprava, urlPath) {
  if (!zprava) return "";
  if (!urlPath) return zprava;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return zprava.replace("{odkaz}", `${origin}${urlPath}`);
}

/* ── OBCHOD ZA ZAKÁZKOU ───────────────────────────────────────────────────
 * Kdo na tom dělá a co komu odešlo NENÍ v zakázce — leží to v OBCHODU
 * (`deals.people`, `deals.activity`), protože obojí existovalo dřív než
 * realitní sekce a poradce to vidí i na kartě obchodu. Druhá kopie by se
 * rozešla první den: makléř by přidal advokáta na kartě zakázky a na kartě
 * obchodu by nebyl.
 */

/** Obchod zakázky. `null` u sirotka — karta to napíše, nespadne. */
export async function nactiObchod(dealId) {
  if (!dealId) return null;
  const { data } = await api.get(`/api/deals/${dealId}`);
  return data && data.id ? data : null;
}

/** Uloží celý seznam lidí. Server pole nahrazuje, ne slučuje. */
export async function ulozTym(dealId, lide) {
  const { data } = await api.patch(`/api/deals/${dealId}`, { people: lide });
  return data;
}

/** Zápis do časové osy obchodu („volal jsem majiteli"). */
export async function zapisDoOsy(dealId, text, typ = "note") {
  const { data } = await api.post(`/api/deals/${dealId}/activity`, { type: typ, text });
  return data;
}

/* ── PROHLÍDKY ────────────────────────────────────────────────────────────
 * Termíny prohlídek drží realitní routa (`/api/reality`), ne zakázky. Karta
 * je jen ČTE, aby v časové ose stálo, kdy které prohlídky byly — vypisují se
 * na obrazovce zájemců, která na to má celou mechaniku (kapacita, odkaz,
 * zápis do diáře).
 *
 * Selhání se nepřevádí na prázdný seznam: „žádná prohlídka" a „prohlídky se
 * nepodařilo načíst" jsou dvě různé věty a makléř má vidět tu pravdivou.
 */
export async function nactiProhlidkyZakazky(zakazkaId) {
  const { data } = await api.get(`/api/reality/zakazky/${zakazkaId}/prohlidky`);
  return Array.isArray(data?.polozky) ? data.polozky.filter(Boolean) : [];
}
